import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";

import { transferWindowService } from "../../services/api";
import { useTranslation } from "../../contexts/TranslationContext";
import useTransferWindowStatus from "../../hooks/common/useTransferWindowStatus";
// Shared flag renderer (name -> flag-icons override + image_url fallback).
// Extracted from this widget so the Agent Tenders dropdown renders the SAME
// clean, correctly-mapped flags. Aliased to FlagSlot to keep call sites below
// unchanged - the home widget's appearance is identical.
import FlagSlot from "../transfer-windows/CountryFlag";

// Home Quick View — Men's Professional only, per product brief.
const CATEGORY = "mens_professional";
const DEFAULT_COUNTRY = "England";

// ── Status badge in widget header — yellow Bauhaus-themed pill ──────────────
const STATUS_LABEL = {
  open: "OPEN NOW",
  soon: "OPENS SOON",
  upcoming: "UPCOMING",
  expected: "EXPECTED SOON",
};
function StatusBadge({ status }) {
  if (!status) return null;
  const label = STATUS_LABEL[status] || "EXPECTED SOON";
  return (
    <span
      className="inline-flex items-center gap-2 rounded-xl px-3 py-1.5 border"
      style={{
        background: "rgba(255, 204, 0, 0.3)",
        borderColor: "#FFCC00",
      }}
    >
      <svg
        width="11"
        height="11"
        viewBox="0 0 12 12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="6" cy="6" r="5" stroke="#1A1A1A" strokeWidth="1.2" />
        <path
          d="M6 3.5V6L7.5 7"
          stroke="#1A1A1A"
          strokeWidth="1.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span
        className="text-[10px] font-bold uppercase text-[#1A1A1A]"
        style={{ letterSpacing: "0.55px" }}
      >
        {label}
      </span>
    </span>
  );
}

// ── Sun icon — summer window ──────────────────────────────────────────────
const SunIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 22 22"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M11 14C11.8333 14 12.5417 13.7083 13.125 13.125C13.7083 12.5417 14 11.8333 14 11C14 10.1667 13.7083 9.45833 13.125 8.875C12.5417 8.29167 11.8333 8 11 8C10.1667 8 9.45833 8.29167 8.875 8.875C8.29167 9.45833 8 10.1667 8 11C8 11.8333 8.29167 12.5417 8.875 13.125C9.45833 13.7083 10.1667 14 11 14ZM11 16C9.61667 16 8.4375 15.5125 7.4625 14.5375C6.4875 13.5625 6 12.3833 6 11C6 9.61667 6.4875 8.4375 7.4625 7.4625C8.4375 6.4875 9.61667 6 11 6C12.3833 6 13.5625 6.4875 14.5375 7.4625C15.5125 8.4375 16 9.61667 16 11C16 12.3833 15.5125 13.5625 14.5375 14.5375C13.5625 15.5125 12.3833 16 11 16ZM4 12H0V10H4V12ZM22 12H18V10H22V12ZM10 4V0H12V4H10ZM10 22V18H12V22H10ZM5.4 6.75L2.875 4.325L4.3 2.85L6.7 5.35L5.4 6.75ZM17.7 19.15L15.275 16.625L16.6 15.25L19.125 17.675L17.7 19.15ZM15.25 5.4L17.675 2.875L19.15 4.3L16.65 6.7L15.25 5.4ZM2.85 17.7L5.375 15.275L6.75 16.6L4.325 19.125L2.85 17.7Z"
      fill="#A67C00"
    />
  </svg>
);

// ── Snow icon — winter window ─────────────────────────────────────────────
const SnowIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9 20V15.85L5.75 19.05L4.35 17.65L9 13V11H7L2.35 15.65L0.95 14.25L4.15 11H0V9H4.15L0.95 5.75L2.35 4.35L7 9H9V7L4.35 2.35L5.75 0.95L9 4.15V0H11V4.15L14.25 0.95L15.65 2.35L11 7V9H13L17.65 4.35L19.05 5.75L15.85 9H20V11H15.85L19.05 14.25L17.65 15.65L13 11H11V13L15.65 17.65L14.25 19.05L11 15.85V20H9Z"
      fill="#003DA8"
    />
  </svg>
);

const ChevronDown = () => (
  <svg
    width="12"
    height="8"
    viewBox="0 0 12 8"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M1 1.5L6 6.5L11 1.5"
      stroke="#1A1A1A"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ArrowRight = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3 7h8M7 3l4 4-4 4"
      stroke="#2C2C2C"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ── Country dropdown ──────────────────────────────────────────────────────
function CountryDropdown({ countries, value, onSelect, loading }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  // Position of the open panel, measured from the trigger. The panel is
  // rendered through a portal to document.body (see below) so it escapes the
  // TransferWindow card's paint order and can never be clipped by the
  // later-sibling rows (e.g. StandingsWidget) that create their own stacking
  // contexts.
  const [panelRect, setPanelRect] = useState(null);
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const inputRef = useRef(null);

  const active = useMemo(
    () => countries.find((c) => c.value === value),
    [countries, value],
  );

  // Measure the trigger and place the panel directly beneath it (fixed coords).
  const reposition = useCallback(() => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    setPanelRect({ top: r.bottom + 6, left: r.left, width: r.width });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    reposition();
    // Outside-click close. Uses mousedown. A mousedown ANYWHERE inside the
    // trigger wrapper OR inside the portaled panel (including on the panel's
    // own native scrollbar, which belongs to a descendant of panelRef) counts
    // as INSIDE and must NOT close. Only a mousedown that is outside both
    // closes. This is what makes clicking/dragging the scrollbar safe.
    const close = (e) => {
      const tgt = e.target;
      const inTrigger = wrapRef.current && wrapRef.current.contains(tgt);
      const inPanel = panelRef.current && panelRef.current.contains(tgt);
      if (!inTrigger && !inPanel) setOpen(false);
    };
    const esc = (e) => e.key === "Escape" && setOpen(false);
    // Scroll handling — BULLETPROOF: we NEVER close the dropdown on a scroll.
    // The panel is fixed-positioned, so:
    //  - A scroll INSIDE the panel (the inner overflow-y-auto country list or
    //    its scrollbar) must be left completely alone — no reposition, no
    //    close — otherwise the user could never scroll the list (the original
    //    regression: it closed on the first wheel tick / scrollbar drag).
    //  - An OUTER page/window scroll would visually drift the fixed panel away
    //    from the trigger, so we only REPOSITION (recompute the trigger rect)
    //    to keep the panel glued to the trigger. We do NOT close.
    //  - The single exception is purely cosmetic: if the trigger has been
    //    scrolled entirely out of the viewport there is nothing to attach to,
    //    so we hide. This never fires on a normal wheel tick (the trigger is
    //    on-screen), so it cannot cause the reported "closes on scroll" bug.
    // Capture phase so we also see the panel's own scroll and can short-circuit.
    const onScroll = (e) => {
      const tgt = e.target;
      // Inside the panel (list / scrollbar) or on the trigger → do nothing.
      if (
        (panelRef.current && panelRef.current.contains(tgt)) ||
        (triggerRef.current && triggerRef.current.contains(tgt))
      ) {
        return;
      }
      // Outer page scroll: keep the panel attached to the trigger.
      if (triggerRef.current) {
        const r = triggerRef.current.getBoundingClientRect();
        // Only hide when the trigger is fully out of the viewport — never on a
        // normal wheel tick while the trigger is still visible.
        if (r.bottom <= 0 || r.top >= window.innerHeight) {
          setOpen(false);
          return;
        }
      }
      reposition();
    };
    // Resize: trigger geometry changes, recompute coords so the panel stays
    // aligned. Never closes.
    const onResize = () => reposition();
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    const t = setTimeout(() => inputRef.current?.focus(), 30);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", esc);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
      clearTimeout(t);
    };
  }, [open, reposition]);
  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return countries;
    return countries.filter((c) =>
      (c.label || c.value).toLowerCase().includes(q),
    );
  }, [countries, query]);

  return (
    <div className="relative" ref={wrapRef}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={loading || countries.length === 0}
        className="flex items-center justify-between w-full bg-[#F7F7F7] border-2 border-[#EAEAEA] rounded-lg px-4 py-2 h-12 hover:bg-[#F1F1F1] disabled:opacity-60 disabled:cursor-not-allowed transition"
      >
        <div className="flex items-center gap-3 min-w-0">
          <FlagSlot
            country={active?.value}
            imageUrl={active?.image_url}
            width={24}
            height={16}
          />
          <span className="text-sm font-bold text-[#1A1A1A] truncate">
            {active?.label ||
              value ||
              (loading ? "Loading …" : "Select country")}
          </span>
        </div>
        <ChevronDown />
      </button>
      {open &&
        panelRect &&
        createPortal(
          <div
            ref={panelRef}
            className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden flex flex-col"
            style={{
              position: "fixed",
              top: panelRect.top,
              left: panelRect.left,
              width: panelRect.width,
              maxHeight: 320,
              zIndex: 9999,
            }}
          >
            <div className="p-2 border-b border-gray-200 flex-shrink-0">
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search country …"
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-900 placeholder-gray-500 outline-none focus:border-blue-500"
              />
            </div>
            <div className="overflow-y-auto overscroll-y-contain flex-1 min-h-0">
              {filtered.length === 0 ? (
                <div className="px-3 py-3 text-gray-500 text-sm">
                  No countries found
                </div>
              ) : (
                filtered.map((c) => {
                  const isActive = c.value === value;
                  return (
                    <button
                      key={c.value}
                      type="button"
                      onClick={() => {
                        onSelect(c.value);
                        setOpen(false);
                      }}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-gray-50 text-gray-800"
                      style={{
                        background: isActive ? "#EFF6FF" : "transparent",
                      }}
                    >
                      <FlagSlot
                        country={c.value}
                        imageUrl={c.image_url}
                        width={20}
                        height={14}
                      />
                      <span className="text-sm font-medium truncate">
                        {c.label}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

// ── Date range formatter used in summer / winter rows ─────────────────────
const DAY_SUFFIX = (n) => {
  if (n >= 11 && n <= 13) return "th";
  const last = n % 10;
  if (last === 1) return "st";
  if (last === 2) return "nd";
  if (last === 3) return "rd";
  return "th";
};
const fmtShort = (d) => {
  if (!d) return null;
  const day = d.getUTCDate();
  const month = d.toLocaleDateString("en-US", {
    month: "short",
    timeZone: "UTC",
  });
  return `${day}${DAY_SUFFIX(day)} ${month}`;
};
const fmtRange = (s, e) => {
  const a = fmtShort(s);
  const b = fmtShort(e);
  if (!a || !b) return "—";
  return `${a} - ${b}`;
};

// ── Summer / Winter pill rows ─────────────────────────────────────────────
function WindowRow({ kind, label, range }) {
  const isSummer = kind === "summer";
  return (
    <div
      className="flex items-center gap-4 rounded-xl px-4 py-3 border"
      style={{
        background: isSummer
          ? "rgba(255, 247, 230, 0.7)"
          : "rgba(238, 244, 255, 0.7)",
        borderColor: isSummer ? "#FFE0B3" : "#D6E3FF",
      }}
    >
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{
          background: isSummer
            ? "rgba(255, 204, 0, 0.2)"
            : "rgba(0, 85, 255, 0.2)",
        }}
      >
        {isSummer ? <SunIcon /> : <SnowIcon />}
      </div>
      <div className="flex flex-col">
        <span
          className="text-[10px] font-bold uppercase"
          style={{
            letterSpacing: "1px",
            color: isSummer ? "#A67C00" : "#003DA8",
          }}
        >
          {label}
        </span>
        <span className="text-base font-bold text-[#1A1A1A]">{range}</span>
      </div>
    </div>
  );
}

// ── Top-level widget ──────────────────────────────────────────────────────
const TransferWindow = () => {
  const navigate = useNavigate();
  const { currentLanguage, translatePage } = useTranslation();
  const apiLanguage = (currentLanguage || "en").toLowerCase().split("-")[0];
  const [countries, setCountries] = useState([]);
  const [countriesLoading, setCountriesLoading] = useState(true);
  const [country, setCountry] = useState(null);
  const countriesAbortRef = useRef(null);
  const widgetRef = useRef(null);

  useEffect(() => {
    if (countriesAbortRef.current) countriesAbortRef.current.abort();
    const ctrl = new AbortController();
    countriesAbortRef.current = ctrl;
    setCountriesLoading(true);
    (async () => {
      try {
        const res = await transferWindowService.getTransferWindowCountries(
          CATEGORY,
          { signal: ctrl.signal },
        );
        if (ctrl.signal.aborted) return;
        if (res.success) {
          const list = res.data || [];
          setCountries(list);
          const def = list.find((c) => c.value === DEFAULT_COUNTRY) || list[0];
          if (def) setCountry((prev) => prev || def.value);
        }
      } catch (e) {
        if (e?.name !== "CanceledError") {
          // UI shows "—" when no country resolves
        }
      } finally {
        if (!ctrl.signal.aborted) setCountriesLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, []);

  const { loading, status, latestSeason, windows } = useTransferWindowStatus(
    country,
    CATEGORY,
  );

  // Re-pass through the global translation engine when async data lands.
  useEffect(() => {
    if (!translatePage) return undefined;
    if ((apiLanguage || "en") === "en") return undefined;
    if (loading || countriesLoading) return undefined;
    const t = setTimeout(() => {
      const el = widgetRef.current;
      if (el) translatePage(apiLanguage, false, el, { force: true });
    }, 150);
    return () => clearTimeout(t);
  }, [windows, status, loading, countriesLoading, apiLanguage, translatePage]);

  // Resolve which window is summer vs winter — by label, falling back to
  // window number 1 = summer / 2 = winter when the label isn't set on a row.
  const summerWindow = useMemo(() => {
    if (!windows || windows.length === 0) return null;
    return windows.find((w) => /summer/i.test(w.label) || w.num === 1) || null;
  }, [windows]);
  const winterWindow = useMemo(() => {
    if (!windows || windows.length === 0) return null;
    return windows.find((w) => /winter/i.test(w.label) || w.num === 2) || null;
  }, [windows]);

  return (
    <div
      ref={widgetRef}
      className="relative overflow-hidden bg-white border border-[#D6DBEA] rounded-lg p-7 h-full flex flex-col gap-4 shadow-[0px_5px_22px_rgba(0,0,0,0.04),0px_0px_0px_0.5px_rgba(0,0,0,0.03)]"
    >
      {/* Bauhaus decorative yellow square (rotated, peeking from top-right) */}
      <div
        aria-hidden="true"
        className="absolute pointer-events-none"
        style={{
          width: 96,
          height: 96,
          right: -50,
          top: -50,
          background: "#FFCC00",
          opacity: 0.2,
          transform: "rotate(45deg)",
        }}
      />

      {/* Season label */}
      <span
        className="text-[14px] uppercase relative z-10"
        style={{ color: "rgba(74, 74, 74, 0.6)", letterSpacing: "0.05em" }}
      >
        Season {latestSeason || "—"}
      </span>

      {/* Heading + status pill */}
      <div className="flex items-center justify-between gap-3 relative z-10 flex-wrap">
        <h3 className="text-base font-bold text-[#1A1A1A] m-0">
          Transfer window for
        </h3>
        <StatusBadge status={status} />
      </div>

      {/* Country dropdown */}
      <div className="relative z-20">
        <CountryDropdown
          countries={countries}
          value={country}
          onSelect={setCountry}
          loading={countriesLoading}
        />
      </div>

      {/* Window rows */}
      <div className="flex flex-col gap-3 relative z-10">
        {loading || countriesLoading ? (
          <>
            <div className="h-[68px] rounded-xl bg-gray-100 animate-pulse" />
            <div className="h-[68px] rounded-xl bg-gray-100 animate-pulse" />
          </>
        ) : (
          <>
            <WindowRow
              kind="summer"
              label="Summer Window"
              range={
                summerWindow
                  ? fmtRange(summerWindow.start, summerWindow.end)
                  : "Not scheduled"
              }
            />
            <WindowRow
              kind="winter"
              label="Winter Window"
              range={
                winterWindow
                  ? fmtRange(winterWindow.start, winterWindow.end)
                  : "Not scheduled"
              }
            />
          </>
        )}
      </div>

      {/* View all transfer windows button */}
      <button
        type="button"
        onClick={() => navigate("/transfer-windows")}
        className="self-start inline-flex items-center gap-2 bg-white border border-[#E6E6E6] rounded-md px-3 py-2.5 text-[#1E1E1E] text-base hover:bg-gray-50 transition relative z-10"
      >
        View all transfer windows
        <ArrowRight />
      </button>
    </div>
  );
};

export default TransferWindow;
